import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { Menu } from 'primereact/menu';

import Layout from '../../Layout/Layout';
import {
  useGetVideoWall,
  useStartWall,
  useStopWall,
  useResyncWall,
  useCalibrateWall,
  useCreatePlaylist,
  useUpdatePlaylist,
  useGetWallMediaLibrary,
} from '../../../queries/videoWall';
import { useGetAssignments } from '../../../queries/wallAssignments';
import { useGetPlaylist } from '../../../queries/wallPlaylistLibrary';
import { useGetThemes } from '../../../queries/signageTemplates';
import GridPreview from './components/GridPreview';
import PlaylistPreviewPlayer from './preview/PlaylistPreviewPlayer';
import ScheduleStatusBanner from './ScheduleStatusBanner';
import useWallSyncTelemetry, { syncHealth, HEALTH_COLORS } from './useWallSyncTelemetry';
import WallLivePreview from './preview/WallLivePreview';
import WallScreenshotComposer from './preview/WallScreenshotComposer';
import ContentVerificationBanner from './preview/ContentVerificationBanner';
import ScheduleScrubber from './preview/ScheduleScrubber';
import PlaylistEditor from './components/PlaylistEditorDialog';
import LiveContentBanner from './components/LiveContentBanner';
import TemplateOrientationWarning from './components/TemplateOrientationWarning';
import ChangePlaylistDialog from './ChangePlaylistDialog';


/* ───────────── Page ───────────── */
export default function VideoWallShow() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [polling, setPolling] = useState(false);
  const [livePreview, setLivePreview] = useState(false);
  const [contentPreview, setContentPreview] = useState(false);
  const [shotComposer, setShotComposer] = useState(false);
  const [schedPreview, setSchedPreview] = useState(false);
  const [changePlaylist, setChangePlaylist] = useState(false);
  const { data: wall, isLoading } = useGetVideoWall(id, true, polling ? 3000 : false);
  const start = useStartWall(id);
  const stop = useStopWall(id);
  const resync = useResyncWall(id);
  const calibrate = useCalibrateWall(id);
  const createPlaylist = useCreatePlaylist(id);
  const updatePlaylist = useUpdatePlaylist(id);

  // Live per-screen sync telemetry straight from RTDB (no polling).
  const telemetry = useWallSyncTelemetry(id);

  const [editor, setEditor] = useState(null); // null | { playlist }
  const [affected, setAffected] = useState([]); // walls playing the just-saved playlist
  const [advanced, setAdvanced] = useState(false); // progressive disclosure for per-screen detail

  // Per-item normalization status chip (full_wall + split carry split_status;
  // media_file / single-cell 'skipped' → no chip). Auto-fired on save.
  const itemStatus = (it) => {
    const s = it.split_status;
    if (!s || s === 'skipped') return null;
    if (s === 'ready') return { key: 'st_ready', color: 'var(--pp-success)' };
    if (s === 'failed') return { key: 'st_error', color: 'var(--pp-danger,#ef4444)' };
    return { key: 'st_processing', color: 'var(--pp-warning)' };
  };

  const screensByPosition = useMemo(() => {
    const map = {};
    (wall?.screens || []).forEach((s) => { map[s.grid_position] = s; });
    return map;
  }, [wall]);

  const playlists = wall?.playlists || [];
  const activePlaylist = playlists.find((p) => p.is_active) || playlists[0] || null;

  // Assignment-aware: a wall with a Content-Library assignment plays the ASSIGNED
  // playlist (the backend resolves assignment over the wall-bound is_active). Surface
  // that here so the wall page shows + starts what will actually play.
  const { data: assignments = [] } = useGetAssignments({ wall_id: id });
  const assignment = assignments[0] || null; // backend picks the scheduled winner on start
  const { data: assignedPlaylist } = useGetPlaylist(assignment?.playlist_id, !!assignment);
  const isAssigned = !!assignment;
  const effectivePlaylist = isAssigned ? assignedPlaylist : activePlaylist;
  const effItems = effectivePlaylist?.items || [];
  const hasContent = effItems.length > 0;

  // Data for the offline "Preview Content" simulation (what Start will play).
  const { data: themeData } = useGetThemes();
  const previewThemes = themeData?.themes ?? [];
  const previewZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const { data: previewMediaRaw } = useGetWallMediaLibrary(true);
  const previewMediaMap = useMemo(() => {
    const arr = previewMediaRaw?.data ?? previewMediaRaw ?? [];
    const m = {}; (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; }); return m;
  }, [previewMediaRaw]);
  const previewResolvePhoto = useMemo(() => (mid) => previewMediaMap[mid]?.path || previewMediaMap[mid]?.thumb || null, [previewMediaMap]);

  // ── Content-row presentation (P1): thumbnail + human label per playlist item ──
  const isVideoUrl = (url) => typeof url === 'string' && /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/i.test(url);
  // A representative thumbnail for the row (library thumb, image URL, or null → glyph).
  const itemThumb = (it) => {
    if (it.content_type === 'split') {
      const firstId = it.screen_assignments && Object.values(it.screen_assignments)[0];
      const m = firstId ? previewMediaMap[firstId] : null;
      return m?.thumb || m?.path || null;
    }
    if (it.media_file_id) {
      const m = previewMediaMap[it.media_file_id];
      return m?.thumb || m?.path || null;
    }
    if (it.file_url && !isVideoUrl(it.file_url)) return it.file_url;
    return null;
  };
  // The real filename / human label (falls back to the content-type name).
  const itemLabel = (it) => {
    if (it.content_type === 'split') {
      const n = it.screen_assignments ? Object.keys(it.screen_assignments).length : 0;
      return t('video_wall.content_split_n', { n });
    }
    if (it.media_file_id && previewMediaMap[it.media_file_id]?.file_name) {
      return previewMediaMap[it.media_file_id].file_name;
    }
    if (it.file_url) return t('video_wall.content_external_url');
    return t(`video_wall.ct_${it.content_type}`);
  };
  const itemGlyph = (it) => (it.content_type === 'template' ? '🎨' : it.content_type === 'split' ? '🔳' : '🎬');

  // Overflow menu (P1): rarely-used / navigate-away actions live behind a ⋯ button
  // so the primary Start/Stop + inspect actions stay visually dominant.
  const overflowRef = React.useRef(null);
  const overflowItems = [
    { label: t('video_wall.shot.title'), icon: 'pi pi-camera', command: () => setShotComposer(true) },
    { label: t('video_wall.sched_prev.title'), icon: 'pi pi-clock', command: () => setSchedPreview(true) },
    { label: t('video_wall.schedule.manage'), icon: 'pi pi-calendar', command: () => navigate(`/apps/video-wall/schedule?wall=${id}`) },
    { label: t('video_wall.run_calibration'), icon: 'pi pi-bolt', command: () => calibrate.mutate() },
  ];

  // Pre-flight readiness: content assigned + every screen has a device.
  const totalCells = (wall?.rows || 1) * (wall?.columns || 1);
  const assignedScreens = (wall?.screens || []).filter((s) => s?.device).length;

  // Any item still normalizing (full_wall pre-split OR split per-screen) → poll so
  // the per-item status chips advance pending/processing → ready without a reload.
  const anyProcessing = effItems.some(
    (it) => it.split_status === 'pending' || it.split_status === 'processing',
  );

  // Poll the wall (split_status) while any full-wall item is still optimizing,
  // but cap it so the button never spins forever if a worker stalls. After the
  // cap we stop polling (the encode may still finish server-side — reload to see).
  // The cap is generous: each cell is a separate ffmpeg encode (~4 min for a
  // 2-cell wall), so a larger grid legitimately takes many minutes — a 5-min cap
  // tripped mid-encode and looked like a failure. 20 min covers realistic walls.
  const OPTIMIZE_POLL_CAP_MS = 1_200_000;
  React.useEffect(() => {
    if (!anyProcessing) { setPolling(false); return undefined; }
    setPolling(true);
    const tmr = setTimeout(() => setPolling(false), OPTIMIZE_POLL_CAP_MS);
    return () => clearTimeout(tmr);
  }, [anyProcessing]);

  // Watchdog: a wall should reach "playing" soon after entering "preparing". If it
  // stays "preparing" past this window (e.g. a device lost the Stop→Start relaunch
  // race), surface an error + Stop/retry instead of spinning forever.
  const PREPARE_WATCHDOG_MS = 30_000;
  const [prepareStuck, setPrepareStuck] = useState(false);
  React.useEffect(() => {
    if (telemetry.state !== 'preparing') { setPrepareStuck(false); return undefined; }
    setPrepareStuck(false);
    const tmr = setTimeout(() => setPrepareStuck(true), PREPARE_WATCHDOG_MS);
    return () => clearTimeout(tmr);
  }, [telemetry.state]);

  const savePlaylist = async (payload) => {
    try {
      let res;
      if (editor?.playlist) {
        res = await updatePlaylist.mutateAsync({ playlistId: editor.playlist.id, ...payload });
      } else {
        res = await createPlaylist.mutateAsync(payload);
      }
      setEditor(null);
      // If this wall is live on the edited playlist, offer a one-click Refresh
      // instead of silently leaving stale content on screen.
      const body = res?.data?.data ?? res?.data ?? res;
      setAffected(Array.isArray(body?.affected_walls) ? body.affected_walls : []);
    } catch { /* toast in hook */ }
  };

  if (isLoading || !wall) {
    return <Layout><Skeleton height="20rem" /></Layout>;
  }

  const playing = wall.state === 'playing';

  return (
    <Layout>
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button icon="pi pi-arrow-left" text rounded onClick={() => navigate('/apps/video-wall/walls')} />
          <div>
            <h3 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{wall.name}</h3>
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              {wall.rows}×{wall.columns} · {t(`video_wall.orientation_${wall.orientation}`)} · {wall.screen_resolution_width}×{wall.screen_resolution_height}
            </small>
          </div>
        </div>
        <span className="pp-status" style={{
          background: playing ? 'var(--pp-success)' : 'var(--pp-bg-tertiary)',
          color: playing ? '#fff' : 'var(--pp-text-secondary)',
          border: 'none', fontWeight: 600, padding: '4px 12px', borderRadius: 999,
        }}>
          {playing ? `● ${t('video_wall.state_playing')}` : t('video_wall.state_stopped')}
        </span>
      </div>

      <div className="row g-3">
        {/* Left — inline content preview + grid preview + sync status */}
        <div className="col-12 col-lg-8">
          {/* P2: live in-page preview of what Start will play — read-only, sync-safe */}
          {hasContent && (
            <div className="p-4 mb-3" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.preview_content')}</h6>
                <Button icon="pi pi-window-maximize" text rounded size="small"
                  aria-label={t('video_wall.preview_content')} onClick={() => setContentPreview(true)} />
              </div>
              <div className="d-flex justify-content-center">
                <PlaylistPreviewPlayer items={effItems} maxWidth={460} lockGrid
                  initialGrid={{ rows: wall?.rows || 1, cols: wall?.columns || 1, orientation: wall?.orientation || 'landscape' }}
                  mediaMap={previewMediaMap} themes={previewThemes} defaultZones={previewZones} resolvePhoto={previewResolvePhoto} />
              </div>
            </div>
          )}

          <div className="p-4" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
            <h6 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.layout_preview')}</h6>
            <div className="d-flex justify-content-center my-3">
              <GridPreview
                rows={wall.rows} columns={wall.columns} orientation={wall.orientation} maxWidth={420}
                renderCell={(r, c, pos) => {
                  const s = screensByPosition[pos];
                  return (
                    <div className="text-center px-1" style={{ lineHeight: 1.2 }}>
                      <div style={{ fontWeight: 700 }}>{pos}</div>
                      <small style={{ fontSize: 9, color: 'var(--pp-text-secondary)' }}>
                        {s?.device?.name || s?.device?.serial_name || t('video_wall.unassigned')}
                      </small>
                    </div>
                  );
                }}
              />
            </div>

            <div className="d-flex align-items-center justify-content-between mt-4">
              <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.sync_status')}</h6>
              {telemetry.state === 'preparing' && !prepareStuck && (
                <span className="small" style={{ color: 'var(--pp-warning)' }}>
                  <i className="pi pi-spin pi-spinner me-1" />
                  {telemetry.cachingScreens > 0
                    ? t('video_wall.caching_screens', { n: telemetry.cachingScreens })
                    : t('video_wall.starting_when_ready')}
                </span>
              )}
              {telemetry.state === 'preparing' && prepareStuck && (
                <span className="small d-inline-flex align-items-center gap-2" style={{ color: 'var(--pp-danger, #ef4444)' }}>
                  <i className="pi pi-exclamation-triangle" />
                  {t('video_wall.sync_failed')}
                  <button type="button" className="btn btn-sm btn-outline-danger py-0 px-2"
                    disabled={stop.isLoading} onClick={() => stop.mutate()}>
                    {t('video_wall.stop_retry')}
                  </button>
                </span>
              )}
              {telemetry.state === 'playing' && telemetry.syncMode && (
                <span
                  className="small d-inline-flex align-items-center gap-1 px-2 py-1"
                  style={{
                    borderRadius: 999,
                    fontWeight: 600,
                    color: telemetry.syncMode === 'lan' ? 'var(--pp-success)' : 'var(--pp-warning)',
                    background: telemetry.syncMode === 'lan'
                      ? 'var(--pp-success-subtle, rgba(34,197,94,.12))'
                      : 'var(--pp-warning-subtle, rgba(245,158,11,.12))',
                  }}
                  title={t(`video_wall.sync_mode_${telemetry.syncMode}_help`)}
                >
                  <i className={telemetry.syncMode === 'lan' ? 'pi pi-wifi' : 'pi pi-cloud'} />
                  {telemetry.syncMode === 'lan'
                    ? t('video_wall.sync_mode_lan', { n: telemetry.masterPos ?? 1 })
                    : t('video_wall.sync_mode_cloud')}
                </span>
              )}
            </div>
            <p className="small mb-2 mt-1" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.sync_note')}</p>
            <button type="button" className="btn btn-link btn-sm p-0 mb-2"
              style={{ textDecoration: 'none', color: 'var(--pp-text-secondary)' }}
              onClick={() => setAdvanced((a) => !a)}>
              <i className={`pi pi-chevron-${advanced ? 'down' : 'right'} me-1`} />{t('video_wall.advanced')}
            </button>
            {advanced && (
            <div className="d-flex flex-column gap-1">
              {(wall.screens || []).map((s) => {
                const tel = telemetry.screens?.[s.grid_position];
                const preparing = telemetry.state === 'preparing';
                const live = telemetry.state === 'playing' && tel && tel.drift_ms != null;
                const health = live ? syncHealth(tel.drift_ms) : 'unknown';
                const cachePct = tel?.cacheProgress;
                const caching = preparing && cachePct != null && cachePct < 100;
                // Content honesty: a device may report a fresh heartbeat yet be showing
                // an error / streaming-stall. content_status overrides the green health.
                const showingError = live && tel?.content_status === 'no_content';
                const buffering = live && tel?.content_status === 'buffering';
                const dotColor = showingError ? 'var(--pp-danger, #ef4444)'
                  : buffering ? 'var(--pp-warning)'
                  : caching ? 'var(--pp-warning)'
                  : HEALTH_COLORS[health];
                return (
                  <div key={s.id} className="p-2"
                    style={{ background: 'var(--pp-bg-secondary)', borderRadius: 8 }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span style={{ color: 'var(--pp-text-primary)' }}>
                        <strong className="me-2">#{s.grid_position}</strong>{s.device?.name || s.device?.serial_name}
                      </span>
                      <span className="d-flex align-items-center gap-2">
                        {showingError ? (
                          <small style={{ color: 'var(--pp-danger, #ef4444)', fontWeight: 600 }}>
                            <i className="pi pi-exclamation-triangle me-1" />{t('video_wall.screen_error')}
                          </small>
                        ) : buffering ? (
                          <small style={{ color: 'var(--pp-warning)', fontWeight: 600 }}>
                            <i className="pi pi-spin pi-spinner me-1" />{t('video_wall.screen_buffering')}
                          </small>
                        ) : live ? (
                          <small style={{ color: HEALTH_COLORS[health], fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {tel.drift_ms > 0 ? '+' : ''}{Math.round(tel.drift_ms)} {t('video_wall.drift_ms')}
                          </small>
                        ) : caching ? (
                          <small style={{ color: 'var(--pp-warning)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {t('video_wall.caching')} {cachePct}%
                          </small>
                        ) : (
                          <small style={{ color: 'var(--pp-text-secondary)' }}>
                            {preparing && (tel?.ready || cachePct === 100)
                              ? t('video_wall.buffering')
                              : t('video_wall.no_telemetry')}
                          </small>
                        )}
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: dotColor,
                        }} title={showingError ? t('video_wall.screen_error') : buffering ? t('video_wall.screen_buffering') : t(`video_wall.${health === 'unknown' ? 'no_telemetry' : health}`)} />
                      </span>
                    </div>
                    {caching && (
                      <div className="mt-2" style={{ height: 6, borderRadius: 999, background: 'var(--pp-bg-tertiary)', overflow: 'hidden' }}>
                        <div style={{ width: `${cachePct}%`, height: '100%', background: 'var(--pp-warning)', transition: 'width .3s ease' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>

        {/* Right — controls + content */}
        <div className="col-12 col-lg-4">
          {/* Content verification (3D) — only shows on a real mismatch */}
          <ContentVerificationBanner wallId={id} />

          {/* Saved content edit while live → one-click Refresh (no auto-interrupt) */}
          <LiveContentBanner walls={affected} onDismiss={() => setAffected([])} />

          {/* Template designed for the opposite orientation of this wall */}
          <TemplateOrientationWarning items={effectivePlaylist?.items} wallOrientation={wall.orientation} />

          {/* Now playing / next scheduled transition */}
          <ScheduleStatusBanner type="wall" id={id} compact />

          <div className="p-4 mb-3" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.controls')}</h6>
              <Button icon="pi pi-ellipsis-v" text rounded size="small"
                aria-label={t('video_wall.more_actions')}
                onClick={(e) => overflowRef.current?.toggle(e)} />
              <Menu model={overflowItems} popup ref={overflowRef} />
            </div>

            {/* Primary action — Start (green) / Stop (red) dominate visually */}
            <div className="d-grid gap-2">
              {playing ? (
                <Button label={t('video_wall.stop')} icon="pi pi-stop-circle" severity="danger" size="large"
                  loading={stop.isLoading} onClick={() => stop.mutate()} />
              ) : (
                <Button label={t('video_wall.start')} icon="pi pi-play" severity="success" size="large"
                  loading={start.isLoading}
                  disabled={!hasContent}
                  onClick={() => start.mutate(isAssigned ? {} : { playlist_id: activePlaylist?.id })} />
              )}

              {/* Pre-flight readiness (before Start) */}
              {!playing && (
                <div className="small px-1 d-flex flex-column gap-1">
                  <span style={{ color: hasContent ? 'var(--pp-success)' : 'var(--pp-warning)' }}>
                    <i className={`pi ${hasContent ? 'pi-check-circle' : 'pi-exclamation-circle'} me-1`} />
                    {hasContent ? t('video_wall.ready_content_ok') : t('video_wall.ready_content_no')}
                  </span>
                  <span style={{ color: assignedScreens >= totalCells ? 'var(--pp-success)' : 'var(--pp-warning)' }}>
                    <i className={`pi ${assignedScreens >= totalCells ? 'pi-check-circle' : 'pi-exclamation-circle'} me-1`} />
                    {t('video_wall.ready_screens', { n: assignedScreens, total: totalCells })}
                  </span>
                </div>
              )}

              {/* Refresh sits with the primary action — only meaningful while playing */}
              {playing && (
                <Button label={t('video_wall.refresh')} icon="pi pi-sync" outlined
                  title={t('video_wall.refresh_help')}
                  loading={resync.isLoading} onClick={() => resync.mutate()} />
              )}
            </div>

            {/* Secondary — "Preview & inspect" cluster (outlined, 2-col grid) */}
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--pp-border)' }}>
              <small className="d-block mb-2" style={{ color: 'var(--pp-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', fontSize: 11 }}>
                {t('video_wall.inspect')}
              </small>
              <div className="row g-2">
                <div className="col-6">
                  <Button label={t('video_wall.preview_content')} icon="pi pi-eye" outlined size="small" className="w-100"
                    onClick={() => setContentPreview(true)} disabled={!hasContent} />
                </div>
                <div className="col-6">
                  <Button label={t('video_wall.live.live_view')} icon="pi pi-wifi" outlined size="small" className="w-100"
                    onClick={() => setLivePreview(true)} disabled={!playing}
                    title={t('video_wall.live.live_view_help')} />
                </div>
              </div>
            </div>
            {isAssigned && (
              <p className="small mt-2 mb-0" style={{ color: 'var(--pp-text-secondary)' }}>
                <i className="pi pi-link me-1" />
                {t('video_wall.assigned_from_library', { name: effectivePlaylist?.name || assignment?.playlist?.name || '…' })}
              </p>
            )}
            {!hasContent && (
              <p className="small mt-2 mb-0" style={{ color: 'var(--pp-warning)' }}>{t('video_wall.empty_content')}</p>
            )}
          </div>

          <div className="p-4" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
            <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
              <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.content')}</h6>
              <div className="d-flex gap-2 flex-wrap">
                <Button label={t('video_wall.change_playlist')} icon="pi pi-arrow-right-arrow-left" size="small" outlined
                  onClick={() => setChangePlaylist(true)} />
                {isAssigned ? (
                  <Button label={t('video_wall.edit_in_library')} icon="pi pi-external-link" size="small" outlined
                    disabled={!assignment?.playlist_id}
                    onClick={() => navigate(`/apps/video-wall/library/${assignment.playlist_id}`)} />
                ) : (
                  <Button label={t('video_wall.edit_content')} icon="pi pi-pencil" size="small"
                    onClick={() => setEditor({ playlist: activePlaylist })} />
                )}
              </div>
            </div>
            {effItems.length === 0 ? (
              <p className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.empty_content')}</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {effItems.map((it, i) => {
                  const stt = itemStatus(it);
                  const thumb = itemThumb(it);
                  const label = itemLabel(it);
                  return (
                    <div key={it.id || i} className="d-flex align-items-center gap-2 p-2"
                      style={{ background: 'var(--pp-bg-secondary)', borderRadius: 8 }}>
                      {/* thumbnail (library thumb / image URL) or content-type glyph */}
                      <div style={{
                        width: 56, height: 40, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
                        background: 'var(--pp-bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {thumb
                          ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 18 }}>{itemGlyph(it)}</span>}
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                          <span style={{ color: 'var(--pp-text-secondary)', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{i + 1}</span>
                          <span className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }} title={label}>{label}</span>
                        </div>
                        <small className="d-block text-truncate" style={{ color: 'var(--pp-text-secondary)' }}>
                          {t(`video_wall.ct_${it.content_type}`)} · {it.duration_seconds}s
                        </small>
                      </div>
                      {stt && (
                        <span className="pp-status" style={{ background: 'transparent', border: 'none', color: stt.color, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                          {t(`video_wall.${stt.key}`)}{stt.key === 'st_ready' ? ' ✓' : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="small mt-2 mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.auto_optimize_note')}</p>
          </div>
        </div>
      </div>

      {editor && (
        <PlaylistEditor
          visible={!!editor}
          onHide={() => setEditor(null)}
          wall={wall}
          playlist={editor.playlist}
          onSave={savePlaylist}
          saving={createPlaylist.isLoading || updatePlaylist.isLoading}
        />
      )}

      <Dialog header={t('video_wall.live.live_view')} visible={livePreview} onHide={() => setLivePreview(false)}
        style={{ width: 'min(96vw, 760px)' }} dismissableMask>
        <div className="d-flex justify-content-center p-2">
          <WallLivePreview wallId={id} maxWidth={680} />
        </div>
      </Dialog>

      {/* Preview Content (offline) — exactly what Start will play, on THIS wall's grid */}
      <Dialog header={t('video_wall.preview_content')} visible={contentPreview} onHide={() => setContentPreview(false)}
        style={{ width: 'min(96vw, 780px)' }} dismissableMask>
        <div className="p-2">
          <p className="small text-muted text-center mb-3">{t('video_wall.preview_content_hint')}</p>
          <div className="d-flex justify-content-center">
            <PlaylistPreviewPlayer items={effItems} maxWidth={700} lockGrid
              initialGrid={{ rows: wall?.rows || 1, cols: wall?.columns || 1, orientation: wall?.orientation || 'landscape' }}
              mediaMap={previewMediaMap} themes={previewThemes} defaultZones={previewZones} resolvePhoto={previewResolvePhoto} />
          </div>
        </div>
      </Dialog>

      <Dialog header={t('video_wall.shot.title')} visible={shotComposer} onHide={() => setShotComposer(false)}
        style={{ width: 'min(96vw, 820px)' }} dismissableMask>
        <div className="p-2">
          <WallScreenshotComposer wall={wall} maxWidth={740} />
        </div>
      </Dialog>

      <Dialog header={t('video_wall.sched_prev.title')} visible={schedPreview} onHide={() => setSchedPreview(false)}
        style={{ width: 'min(96vw, 720px)' }} dismissableMask>
        <div className="p-2">
          <ScheduleScrubber wallId={id} grid={{ rows: wall?.rows || 1, cols: wall?.columns || 1 }} maxWidth={620} />
        </div>
      </Dialog>

      <ChangePlaylistDialog
        visible={changePlaylist}
        onHide={() => setChangePlaylist(false)}
        wallId={id}
        currentPlaylistId={assignment?.playlist_id || null}
        assignmentId={assignment?.id || null}
      />
    </Layout>
  );
}
